import { RecaptchaError } from '@hub/lib/recaptcha';
import type { SentryFlow } from '@hub/lib/sentry-flow';
import { RECAPTCHA_ACTIONS, type RecaptchaHeaders, type RecaptchaProtectedOperation } from '@platform/api';
import { captureException, withScope } from '@platform/observability';
import { type UseMutationOptions, type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useRecaptcha } from './useRecaptcha';

/** The action the BFF pins the given operation to. */
type ActionOf<K extends RecaptchaProtectedOperation> = (typeof RECAPTCHA_ACTIONS)[K];

type RecaptchaMutationFn<TData, TVariables, K extends RecaptchaProtectedOperation> = (
  vars: TVariables,
  opts: { recaptcha: RecaptchaHeaders<ActionOf<K>> | undefined },
) => Promise<TData>;

// Match each engine's wording for "tried to access a prop on undefined":
//   - Chromium: "Cannot read properties of undefined (reading 'x')"
//   - Safari:   "undefined is not an object (evaluating 'x.y')"
//   - Firefox:  "x is undefined"
const UNDEFINED_PROP_ACCESS = /Cannot read prop.* of undefined|undefined is not an object|\bis undefined\b/i;

function isGrecaptchaCorruption(cause: unknown): boolean {
  return cause instanceof TypeError && UNDEFINED_PROP_ACCESS.test(cause.message);
}

/**
 * Wraps `useMutation` so that a fresh reCAPTCHA token is fetched before each
 * call. The consumer's `mutationFn` receives the headers as the second arg
 * and forwards them to the underlying API call.
 *
 * Errors from reCAPTCHA execution are reported to Sentry (tagged with both
 * the user-facing `flow` and the technical `recaptcha_action`) and surfaced
 * through `useMutation`'s `onError` as `RecaptchaError`.
 *
 * Takes the *operation* rather than an action string: the action is looked up in
 * the generated RECAPTCHA_ACTIONS map, so it can never drift from what the BFF
 * pins server-side, and the token handed to `mutationFn` is typed to that action —
 * the generated wrapper for a different endpoint will not accept it.
 *
 * ```tsx
 * const { mutate, isPending } = useRecaptchaMutation(
 *   'reAuthenticate',
 *   'step_up_password',
 *   ({ password }: FormValues, { recaptcha }) =>
 *     reAuthenticateWithRecaptcha({ body: { password }, meta: { recaptcha } }),
 *   { onSuccess, onError, meta: { errorHandling: 'self-reported' } },
 * );
 * ```
 */
export function useRecaptchaMutation<K extends RecaptchaProtectedOperation, TData, TError = Error, TVariables = void, TContext = unknown>(
  operation: K,
  flow: SentryFlow,
  mutationFn: RecaptchaMutationFn<TData, TVariables, K>,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const executeRecaptcha = useRecaptcha();
  // Looked up, never passed in: the map is generated from the same x-recaptcha-action
  // the BFF enforces, so the two cannot drift.
  const action = RECAPTCHA_ACTIONS[operation];

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: async (vars) => {
      let recaptcha: RecaptchaHeaders<ActionOf<K>> | undefined;
      try {
        recaptcha = await executeRecaptcha(action);
      } catch (cause) {
        if (cause instanceof RecaptchaError) {
          withScope((scope) => {
            scope.setTag('flow', flow);
            scope.setTag('recaptcha_action', action);
            // Browser extensions and translation engines mutate the DOM under grecaptcha's
            // feet, corrupting its internal client registry mid-execute. The resulting
            // TypeError ("Cannot read properties of undefined…") is environmental, not a
            // first-party bug — tag so triage can filter. See HUB-3Y.
            if (isGrecaptchaCorruption(cause.cause)) {
              scope.setTag('recaptcha_third_party_corruption', true);
            }
            captureException(cause);
          });
        }
        throw cause;
      }
      return mutationFn(vars, { recaptcha });
    },
  });
}
